/**
 * Tổng quan — `index.html`, FR-010…013.
 *
 * Bố cục bám prototype: `page-head` · `section.grid.g-4` bốn thẻ `.card.stat` ·
 * `div.grid.g-side` gồm bảng "Tên miền mới cập nhật" (`.table-wrap > table.tbl`)
 * và `aside.stack` chứa "Việc cần làm" (`ol.timeline`) + "Hoạt động gần đây".
 *
 * Bỏ khối "Các màn hình trong prototype" của `index.html`: đó là mục lục của bản
 * mẫu, không phải tính năng sản phẩm (docs/11 §11.8.1).
 */

import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { api } from '../lib/api';
import { useAuth } from '../lib/auth';
import { useT } from '../lib/i18n';
import { DOMAIN_STATUS_CHIP } from '../lib/status';
import { avatarTone, initialsOf, useFormat } from '../lib/format';
import { Icon } from '../components/Icon';
import { AsyncSection, EmptyState, SkeletonCard } from '../components/States';

// Hợp đồng `GET /dashboard/summary` nay nằm ở `lib/types.ts` vì `AppShell` cũng
// dùng (FR-003 · bộ đếm sidebar). Một chỗ khai, hai nơi đọc.
import type { DashboardSummary } from '../lib/types';

export function OverviewPage() {
  const { formatRelative, formatNumber } = useFormat();
  const t = useT();
  const { user, can, loading, branding } = useAuth();

  const summaryQuery = useQuery({
    queryKey: ['dashboard', 'summary'],
    queryFn: () => api.get<DashboardSummary>('/dashboard/summary'),
  });

  if (loading || !user) return <SkeletonCard rows={5} />;

  return (
    <div data-od-id="page-overview">
      <div className="page-head">
        <div className="grow">
          <span className="eyebrow">
            {branding.appName ?? t('app.name')} · {branding.tagline ?? t('app.tagline')}
          </span>
          <h1 style={{ marginTop: 6 }} data-od-id="overview-title">
            {t('overview.greeting', { name: user.fullName || user.email })}
          </h1>
          <p className="lede">
            {summaryQuery.data?.scopeAll
              ? t('overview.ledeAdmin', {
                  domains: formatNumber(summaryQuery.data.stats.domains),
                })
              : t('overview.ledeUser', {
                  domains: formatNumber(summaryQuery.data?.stats.domains ?? 0),
                })}
          </p>
        </div>
        {can('dom.add') ? (
          <div className="row">
            {/*
              OQ-251 — `?add=1` mở thẳng hộp thoại thêm tên miền.
              Trước đây nút này chỉ sang `/domains`, và người dùng phải bấm
              "Thêm tên miền" lần thứ hai ở đó: một nút, hai lần bấm, cùng một ý
              định.
            */}
            <Link className="btn btn-primary" to="/domains?add=1" data-od-id="overview-cta">
              <Icon name="plus" size={16} />
              {t('domains.add')}
            </Link>
          </div>
        ) : null}
      </div>

      <AsyncSection
        loading={summaryQuery.isLoading}
        error={summaryQuery.error}
        data={summaryQuery.data}
        onRetry={() => void summaryQuery.refetch()}
        skeleton={<SkeletonCard rows={6} />}
      >
        {(summary) => (
          <>
            <section className="grid g-4" data-od-id="overview-stats">
              <div className="card stat">
                <div className="label">{t('overview.stat.domains')}</div>
                <div className="value num">{formatNumber(summary.stats.domains)}</div>
                <div className="delta">{t('overview.stat.domainsSub')}</div>
              </div>
              <div className="card stat">
                <div className="label">{t('overview.stat.records')}</div>
                <div className="value num">{formatNumber(summary.stats.records)}</div>
                <div className="delta">{t('overview.stat.recordsSub')}</div>
              </div>
              <div className="card stat">
                <div className="label">{t('overview.stat.attention')}</div>
                <div className="value num">{formatNumber(summary.stats.attention)}</div>
                <div className={summary.stats.attention > 0 ? 'delta warn' : 'delta'}>
                  {t('overview.stat.attentionSub')}
                </div>
              </div>
              <div className="card stat">
                <div className="label">{t('overview.stat.connections')}</div>
                <div className="value num">{formatNumber(summary.stats.connections)}</div>
                <div className="delta">{t('overview.stat.connectionsSub')}</div>
              </div>
            </section>

            {/* FR-013 — tài khoản mới: ba bước có nút hành động. */}
            {summary.onboarding.hasDomain ? null : (
              <section className="card mt-20" data-od-id="overview-onboarding">
                <div className="card-head">
                  <div className="grow">
                    <h2>{t('overview.start.title')}</h2>
                    <div className="sub">{t('overview.start.sub')}</div>
                  </div>
                </div>
                <div className="card-body">
                  <ol className="timeline">
                    <OnboardingStep
                      done={summary.onboarding.hasConnection}
                      title={t('overview.start.step1')}
                      body={t('overview.start.step1Body')}
                      to="/connections"
                      cta={t('nav.connections')}
                    />
                    <OnboardingStep
                      done={summary.onboarding.hasDomain}
                      title={t('overview.start.step2')}
                      body={t('overview.start.step2Body')}
                      to="/domains?add=1"
                      cta={t('domains.add')}
                    />
                    <OnboardingStep
                      done={summary.onboarding.hasSharedDomain}
                      title={t('overview.start.step3')}
                      body={t('overview.start.step3Body')}
                      to="/permissions"
                      cta={t('nav.permissions')}
                    />
                  </ol>
                </div>
              </section>
            )}

            <div className="grid g-side mt-20">
              <section className="card" data-od-id="overview-recent">
                <div className="card-head">
                  <div className="grow">
                    <h2>{t('overview.recent.title')}</h2>
                    <div className="sub">{t('overview.recent.sub')}</div>
                  </div>
                  <Link className="btn btn-sm" to="/domains">
                    {t('overview.recent.all')}
                  </Link>
                </div>
                {summary.recentDomains.length === 0 ? (
                  <div className="card-body">
                    <EmptyState icon="domains" title={t('domains.emptyTitle')} />
                  </div>
                ) : (
                  <div className="table-wrap">
                    <table className="tbl" data-od-id="overview-recent-table">
                      <thead>
                        <tr>
                          <th scope="col">{t('domains.colName')}</th>
                          <th scope="col">{t('domains.colStatus')}</th>
                          <th scope="col" className="right">
                            {t('domains.colRecords')}
                          </th>
                          <th scope="col" className="right">
                            {t('domains.colUpdated')}
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        {summary.recentDomains.map((domain) => (
                          <tr key={domain.id}>
                            <td className="mono">
                              <Link to={`/domains/${domain.id}`}>{domain.name}</Link>
                            </td>
                            <td>
                              <span className={`chip ${DOMAIN_STATUS_CHIP[domain.status] ?? ''}`}>
                                <span className="dot" />
                                {t(`domains.status.${domain.status}`)}
                              </span>
                            </td>
                            <td className="right num small">{formatNumber(domain.recordCount)}</td>
                            <td className="right num small">{formatRelative(domain.updatedAt)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </section>

              <aside className="stack">
                <div className="card" data-od-id="overview-alerts">
                  <div className="card-head">
                    <div className="grow">
                      <h2>{t('overview.alerts.title')}</h2>
                      <div className="sub">{t('overview.alerts.sub')}</div>
                    </div>
                  </div>
                  <div className="card-body">
                    {summary.alerts.length === 0 ? (
                      <EmptyState icon="check" title={t('overview.alerts.empty')} />
                    ) : (
                      <ol className="timeline">
                        {summary.alerts.map((alert) => (
                          <li key={alert.kind} className={`tl-item ${alert.level}`}>
                            <div className="when">{t(`overview.alert.${alert.kind}.when`)}</div>
                            <b>
                              {t(`overview.alert.${alert.kind}.title`, {
                                count: formatNumber(alert.count),
                              })}
                            </b>
                            <div className="small muted">
                              {alert.sample ? (
                                <span className="mono">{alert.sample}</span>
                              ) : (
                                t(`overview.alert.${alert.kind}.body`)
                              )}
                            </div>
                            <Link className="btn btn-sm mt-10" to={alert.link}>
                              {t('common.view')}
                            </Link>
                          </li>
                        ))}
                      </ol>
                    )}
                  </div>
                </div>

                <div className="card" data-od-id="overview-activity">
                  <div className="card-head">
                    <div className="grow">
                      <h2>{t('overview.activity.title')}</h2>
                    </div>
                    <Link className="btn btn-sm btn-ghost" to="/audit-log">
                      {t('nav.audit')}
                    </Link>
                  </div>
                  <div className="card-body">
                    {summary.recentActivity.length === 0 ? (
                      <EmptyState icon="audit" title={t('overview.activity.empty')} />
                    ) : (
                      <ul className="bell-list">
                        {summary.recentActivity.map((item) => (
                          <li key={item.id}>
                            <span className="activity-row">
                              <span
                                className={`avatar sm ${avatarTone(item.actorEmail ?? 'system')}`}
                              >
                                {initialsOf('', item.actorEmail ?? 'HT')}
                              </span>
                              <span className="who">
                                <b>{t(`action.${item.action}`)}</b>
                                <span className="small muted">
                                  {item.targetLabel ?? '—'} · {formatRelative(item.createdAt)}
                                </span>
                              </span>
                            </span>
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>
                </div>
              </aside>
            </div>
          </>
        )}
      </AsyncSection>
    </div>
  );
}

function OnboardingStep({
  done,
  title,
  body,
  to,
  cta,
}: {
  done: boolean;
  title: string;
  body: string;
  to: string;
  cta: string;
}) {
  return (
    <li className={`tl-item ${done ? 'ok' : 'warn'}`}>
      <div className="when">{done ? '✓' : '•'}</div>
      <b>{title}</b>
      <div className="small muted">{body}</div>
      {done ? null : (
        <Link className="btn btn-sm mt-10" to={to}>
          {cta}
        </Link>
      )}
    </li>
  );
}
